import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import { ApiError } from '@formosa/shared/api/request';
import SettingsSubPageShell from '../../components/settings/SettingsSubPageShell';
import { useAuth } from '../../auth/AuthContext';
import { useTranslation } from '../../i18n/LocaleContext';
import { canShowBackfillInviter } from '../../utils/memberInviter';

export default function BackfillInviterCodePage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { t } = useTranslation();

  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviterCode, setInviterCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const info = await memberApi.getInfo();
        if (!cancelled) {
          setMember(info || null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || t('backfillInviter.loadFailed'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const eligible = canShowBackfillInviter(member);

  async function handleSubmit(event) {
    event.preventDefault();
    if (!eligible || submitting) {
      return;
    }

    const code = inviterCode.trim();
    if (!code) {
      setError(t('backfillInviter.codeRequired'));
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const result = await memberApi.backfillInviterCode({ inviterCode: code });
      await refreshUser();
      const inviterLabel = result?.inviterUsername || result?.inviterPromotionCode || code;
      setSuccess(
        t('backfillInviter.success').replace('{inviter}', inviterLabel)
      );
      window.setTimeout(() => navigate(-1), 1500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('backfillInviter.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  const deadline = member?.registerVerifyBackfillDeadline
    ? String(member.registerVerifyBackfillDeadline).replace('T', ' ').slice(0, 19)
    : '';

  return (
    <SettingsSubPageShell title={t('backfillInviter.title')}>
      <div className="backfill-inviter-page">
        {loading ? <p className="backfill-inviter-page__status">{t('common.loading')}</p> : null}

        {!loading && !eligible ? (
          <p className="backfill-inviter-page__status backfill-inviter-page__status--muted">
            {t('backfillInviter.notAvailable')}
          </p>
        ) : null}

        {!loading && eligible ? (
          <>
            <p className="backfill-inviter-page__hint">{t('backfillInviter.hint')}</p>
            {deadline ? (
              <p className="backfill-inviter-page__deadline">
                {t('backfillInviter.deadline').replace('{deadline}', deadline)}
              </p>
            ) : null}

            <form className="backfill-inviter-page__form" onSubmit={handleSubmit}>
              <label htmlFor="backfillInviterCode">{t('backfillInviter.codeLabel')}</label>
              <input
                id="backfillInviterCode"
                type="text"
                value={inviterCode}
                placeholder={t('backfillInviter.codePlaceholder')}
                onChange={(e) => setInviterCode(e.target.value)}
                autoComplete="off"
              />

              {error ? <p className="backfill-inviter-page__error">{error}</p> : null}
              {success ? <p className="backfill-inviter-page__success">{success}</p> : null}

              <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
                {submitting ? t('common.loading') : t('backfillInviter.submit')}
              </button>
            </form>
          </>
        ) : null}
      </div>
    </SettingsSubPageShell>
  );
}
